import { styled } from "@linaria/react"
import { useContext } from "react"

import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { Horizontal } from "@/components/Flex.ts"
import { useQueries } from "@/hooks/useRootContext.ts"
import { isNullish } from "@/lib/std.ts"
import { TaskViewModelContext } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import { smallerFontSize } from "@/styles/textStyles.ts"

import type { AiChatMessageContent, RepositoryAuthType } from "@/lib/client/generated/TypeDefinitions.ts"


export const RepositoryAuthTypeSelectorMessage = (props: {
  taskId: string
  content: AiChatMessageContent.InitProject.RepositoryAuthTypeSelector
  isActive: boolean
}) => {
  const queries = useQueries()
  const types = props.content.supportedAuthTypes
  const taskModel = useContext(TaskViewModelContext)
  const step = taskModel?.initProjectStep?.get()
  const disabled = isNullish(step) || taskModel?.agentIsRunning.get()

  types.sort((a, b) => authTypeOrder.indexOf(a) - authTypeOrder.indexOf(b))
  return (
    <AgentMessageContainer isActive={props.isActive}>
      <Horizontal gap="16px" justifyContent="stretch">
        {types.map(it => {
          return (
            <Card
              key={it}
              onClick={(e) => {
                if (!disabled) {
                  e.preventDefault()
                  void queries.apiClient.setRepositoryAccess(props.taskId, it)
                }
              }}
              selected={it === props.content.selectedAuthType}
              disabled={disabled || (it === props.content.selectedAuthType)}
              data-testid={nameByType[it]}
            >
              <Horizontal alignItems="center" justifyContent="space-between" gap="8px">
                <Title>
                  {nameByType[it]}
                </Title>
                {isRecommended(it, props.content.supportedAuthTypes) && (
                  <Tag>
                    RECOMMENDED
                  </Tag>
                )}
              </Horizontal>

              <Description>
                {descriptionByType[it]}
              </Description>
            </Card>
          )
        })}
      </Horizontal>
    </AgentMessageContainer>
  )
}

const descriptionByType: Record<RepositoryAuthType, string> = {
  "Anonymous": "Only works with public repositories\nNo authentication required\nQuick setup for public repos",
  "Token": "Most secure and flexible\nFine-grained permissions\nRequires an access token with read access",
  "Basic": "Simple username and password authentication\nEasy to set up but less secure\nNot recommended if token or SSH is available",
  "SSH": "Secure and widely used by developers\nUses SSH keys instead of passwords\nRequires setting up a public/private key pair"
}

const nameByType: Record<RepositoryAuthType, string> = {
  "Anonymous": "Anonymous",
  "Token": "Personal Access Token",
  "Basic": "Username & Password",
  "SSH": "SSH Key"
}


const authTypeOrder: RepositoryAuthType[] = ["Token", "SSH", "Basic", "Anonymous"]

function isRecommended(type: RepositoryAuthType, otherTypes: RepositoryAuthType[]) {
  if (type === "Token") {
    return true
  }

  if (type === "SSH") {
    return !otherTypes.includes("Token")
  }

  return false
}

const Tag = styled.span`
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0.5px;

  border-radius: var(--border-radius-8);
  padding: 4px 6px;

  color: var(--main-color);
  background-color: var(--main-color-15);
`

const Card = styled.div<{ selected?: boolean, disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;

  border: 1px solid var(--main-color-10);
  border-radius: var(--border-radius-20);
  background-color: ${props => props.selected ? "var(--main-color-5)" : "transparent"};
  padding: 16px;

  box-shadow: 0 2px 10px 0 var(--text-color-5);

  cursor: ${props => props.disabled ? "default" : "pointer"};

  &:hover {
    background-color: ${props => props.disabled ? (props.selected ? "var(--main-color-5)" : "transparent") : "var(--main-color-10)"};
  }
`

const Title = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`

const Description = styled.div`
  ${smallerFontSize};

  color: var(--secondary-text-color);

  white-space: pre-wrap;
`